import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Play, ArrowRight, ArrowUpRight } from "lucide-react";
import { fetchEpisodes } from "@/lib/api";
import { useContent } from "@/lib/useContent";
import { MaskedLine, Reveal } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import EpisodeCard from "@/components/EpisodeCard";
import SubmissionForm from "@/components/SubmissionForm";

const LOGO_URL =
    "https://customer-assets-7cd3h4nn.emergentagent.net/job_7890e65a-8a22-475d-8f20-42607090552b/artifacts/0cc171a306344fa8_IMG_9558.jpg";
const COLLAGE_URL =
    "https://customer-assets-7cd3h4nn.emergentagent.net/job_7890e65a-8a22-475d-8f20-42607090552b/artifacts/c462948d0989184f_Gemini_Generated_Image_uank44uank44uank%20%281%29.webp";
const SOUNDS_GAY_URL =
    "https://customer-assets-7cd3h4nn.emergentagent.net/job_7890e65a-8a22-475d-8f20-42607090552b/artifacts/9b4e96066cc9b252_Gemini_Generated_Image_ojunl3ojunl3ojun.webp";

export default function Home() {
    const [episodes, setEpisodes] = useState([]);
    const { settings } = useContent();
    const { scrollY } = useScroll();
    const artY = useTransform(scrollY, [0, 700], [0, 90]);

    useEffect(() => {
        fetchEpisodes().then(setEpisodes).catch(() => {});
    }, []);

    const latest = episodes[0];

    return (
        <div data-testid="home-page">
            {/* ---------- HERO ---------- */}
            <section className="relative overflow-hidden bg-paper border-b-2 border-ink">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-14 pb-20 sm:pt-20 sm:pb-28 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
                    <div className="lg:col-span-7">
                        <motion.p
                            initial={{ opacity: 0, y: 14 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            data-testid="hero-eyebrow"
                            className="font-mono-brand text-xs uppercase tracking-[0.25em] text-pink"
                        >
                            BOMGAY.in | A queer cultural podcast from Bombay
                        </motion.p>
                        <h1
                            data-testid="hero-heading"
                            className="mt-6 font-display font-black tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl text-ink"
                        >
                            <MaskedLine delay={0.15}>People.</MaskedLine>
                            <MaskedLine delay={0.3} className="italic text-pink">
                                Places.
                            </MaskedLine>
                            <MaskedLine delay={0.45}>Histories.</MaskedLine>
                            <MaskedLine delay={0.6} className="italic text-pink">
                                Desires.
                            </MaskedLine>
                        </h1>
                        <motion.p
                            initial={{ opacity: 0, y: 18 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.85 }}
                            className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-ink/80"
                        >
                            {settings.hero_intro}
                        </motion.p>
                        <motion.div
                            initial={{ opacity: 0, y: 18 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 1 }}
                            className="mt-9 flex flex-wrap items-center gap-4"
                        >
                            {latest ? (
                                <Link
                                    data-testid="hero-listen-button"
                                    to={`/episodes/${latest.id}`}
                                    className="inline-flex items-center gap-2.5 bg-pink text-cream font-sub font-bold px-7 py-4 border-2 border-ink hard-shadow transition-transform duration-200 hover:-translate-y-1"
                                >
                                    <Play className="w-5 h-5" fill="currentColor" />
                                    Listen to the Latest Episode
                                </Link>
                            ) : (
                                <Link
                                    data-testid="hero-listen-button"
                                    to="/episodes"
                                    className="inline-flex items-center gap-2.5 bg-pink text-cream font-sub font-bold px-7 py-4 border-2 border-ink hard-shadow transition-transform duration-200 hover:-translate-y-1"
                                >
                                    <Play className="w-5 h-5" fill="currentColor" />
                                    Listen to the Latest Episode
                                </Link>
                            )}
                            <Link
                                data-testid="hero-archive-link"
                                to="/episodes"
                                className="inline-flex items-center gap-2 font-sub font-bold px-7 py-4 border-2 border-ink bg-paper text-ink transition-colors duration-200 hover:bg-mustard/30"
                            >
                                Explore the Archive <ArrowRight className="w-4 h-4" />
                            </Link>
                        </motion.div>
                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.8, delay: 1.2 }}
                            className="mt-8 font-mono-brand text-xs uppercase tracking-[0.2em] text-ink/50"
                        >
                            Hosted by Sagar Malkar, Filmmaker, Media Entrepreneur and Storyteller
                        </motion.p>
                    </div>

                    <div className="lg:col-span-5 relative">
                        <motion.div
                            style={{ y: artY }}
                            initial={{ opacity: 0, rotate: -6, scale: 0.94 }}
                            animate={{ opacity: 1, rotate: -2, scale: 1 }}
                            transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
                            className="relative"
                        >
                            <img
                                src={COLLAGE_URL}
                                alt="BOMGAY collage: Gateway of India, taxi and the sea"
                                data-testid="hero-collage-art"
                                className="w-full border-2 border-ink hard-shadow-pink object-cover aspect-square"
                            />
                            <motion.img
                                src={LOGO_URL}
                                alt="BOMGAY logo sticker"
                                animate={{ rotate: [5, 8, 5] }}
                                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                                className="absolute -top-7 -left-5 w-24 sm:w-28 border-2 border-ink hard-shadow-sm bg-cream"
                            />
                            <motion.div
                                animate={{ rotate: [-4, -1.5, -4] }}
                                transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
                                className="absolute -bottom-6 -right-3 bg-ink text-paper font-sub font-bold text-sm px-4 py-2.5 border-2 border-ink hard-shadow-sm"
                            >
                                Same city. Different times. Same love.
                            </motion.div>
                        </motion.div>
                    </div>
                </div>
            </section>

            <Marquee />

            {/* ---------- LATEST EPISODES ---------- */}
            <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-20 sm:pt-28">
                <Reveal className="flex flex-wrap items-end justify-between gap-4">
                    <div>
                        <p className="font-mono-brand text-xs uppercase tracking-[0.25em] text-pink">
                            Fresh off the press
                        </p>
                        <h2 className="mt-3 font-display font-black text-2xl sm:text-3xl lg:text-4xl text-ink tracking-tight">
                            Latest Episodes
                        </h2>
                        <p className="mt-3 max-w-lg text-sm sm:text-base leading-relaxed text-ink/70">
                            Good conversations stay with you. Great stories deserve to be remembered.
                        </p>
                    </div>
                    <Link
                        to="/episodes"
                        data-testid="latest-view-all-link"
                        className="inline-flex items-center gap-1.5 font-sub font-bold text-ink hover:text-pink transition-colors duration-150"
                    >
                        Explore All Episodes <ArrowUpRight className="w-4 h-4" />
                    </Link>
                </Reveal>
                <div className="mt-10 grid sm:grid-cols-2 gap-10 lg:gap-14 max-w-4xl">
                    {episodes.map((ep, i) => (
                        <EpisodeCard key={ep.id} ep={ep} index={i} />
                    ))}
                </div>
            </section>

            {/* ---------- THE SHOW ---------- */}
            <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-24 sm:pt-32">
                <div className="grid lg:grid-cols-12 gap-12 items-center">
                    <Reveal className="lg:col-span-5 order-2 lg:order-1">
                        <div className="relative -rotate-2">
                            <img
                                src={SOUNDS_GAY_URL}
                                alt="'Sounds gay, I'm in' illustration"
                                data-testid="about-strip-illustration"
                                className="w-full border-2 border-ink hard-shadow object-cover aspect-square"
                            />
                            <span className="absolute -top-4 -right-3 rotate-6 bg-pink text-cream font-sub font-bold text-xs px-3 py-2 border-2 border-ink hard-shadow-sm">
                                Sounds gay? I'm in.
                            </span>
                        </div>
                    </Reveal>
                    <div className="lg:col-span-7 order-1 lg:order-2">
                        <Reveal>
                            <p className="font-mono-brand text-xs uppercase tracking-[0.25em] text-pink">
                                The show
                            </p>
                            <h2 className="mt-3 font-display font-black text-2xl sm:text-3xl lg:text-4xl text-ink tracking-tight leading-tight">
                                Intimate conversations. Oral histories. Cultural inquiry.
                            </h2>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <div className="mt-6 max-w-2xl space-y-4 text-base sm:text-lg leading-relaxed text-ink/80">
                                <p>
                                    From identity, love and intimacy to politics, caste, class,
                                    activism, desire and belonging, BOMGAY brings queer voices
                                    across generations and communities into conversation.
                                </p>
                                <p>
                                    We revisit the places that shaped us, listen to the people
                                    who lived through change, and preserve the memories that
                                    might otherwise disappear.
                                </p>
                                <p>
                                    Because queer history lives in more than headlines and
                                    milestones. It lives in friendships, family conversations,
                                    bedrooms, bars, streets, kitchens and the everyday business
                                    of becoming ourselves.
                                </p>
                            </div>
                        </Reveal>
                        <Reveal delay={0.2}>
                            <Link
                                to="/about"
                                data-testid="about-strip-link"
                                className="mt-8 inline-flex items-center gap-2 bg-ink text-paper font-sub font-bold px-7 py-4 border-2 border-ink hard-shadow-pink transition-transform duration-200 hover:-translate-y-1"
                            >
                                Meet the Host <ArrowRight className="w-4 h-4" />
                            </Link>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* ---------- VOICES PREVIEW ---------- */}
            <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-24 sm:pt-32">
                <Reveal className="flex flex-wrap items-end justify-between gap-4">
                    <div>
                        <p className="font-mono-brand text-xs uppercase tracking-[0.25em] text-pink">
                            The living archive
                        </p>
                        <h2 className="mt-3 font-display font-black text-2xl sm:text-3xl lg:text-4xl text-ink tracking-tight">
                            Voices on the Record
                        </h2>
                        <p className="mt-3 max-w-xl text-sm sm:text-base leading-relaxed text-ink/70">
                            Every voice holds a piece of the story. Each conversation adds
                            another perspective to a history that is still being written.
                        </p>
                    </div>
                    <Link
                        to="/voices"
                        data-testid="voices-view-all-link"
                        className="inline-flex items-center gap-1.5 font-sub font-bold text-ink hover:text-pink transition-colors duration-150"
                    >
                        Explore All Voices <ArrowUpRight className="w-4 h-4" />
                    </Link>
                </Reveal>
                <div className="mt-10 grid sm:grid-cols-3 gap-8">
                    {episodes.map((ep, i) => (
                        <Reveal key={ep.id} delay={i * 0.1}>
                            <div
                                data-testid={`voice-preview-${ep.id}`}
                                className={`bg-cream border-2 border-ink hard-shadow-sm p-6 ${
                                    i % 2 ? "rotate-1" : "-rotate-1"
                                } transition-transform duration-300 hover:rotate-0`}
                            >
                                <p className="font-mono-brand text-xs uppercase tracking-[0.2em] text-mustard">
                                    EP {ep.number}: {ep.title}
                                </p>
                                <p className="mt-3 font-display font-black text-2xl text-ink">
                                    {ep.guest}
                                </p>
                                <p className="mt-1 font-sub italic text-sm text-ink/70">
                                    {ep.guest_role}
                                </p>
                                <p className="mt-4 text-sm leading-relaxed text-ink/80 border-l-4 border-pink pl-4">
                                    {ep.guest_quote}
                                </p>
                            </div>
                        </Reveal>
                    ))}
                    <Reveal delay={0.2}>
                        <Link
                            to="/voices"
                            data-testid="voice-preview-cta"
                            className="flex flex-col justify-between h-full min-h-[220px] bg-ink text-paper border-2 border-ink hard-shadow-sm p-6 rotate-1 transition-transform duration-300 hover:rotate-0 hover:-translate-y-1"
                        >
                            <p className="font-display font-black text-2xl leading-snug">
                                Your story could be next.
                            </p>
                            <span className="mt-6 inline-flex items-center gap-2 font-sub font-bold text-mustard">
                                Step into the archive <ArrowRight className="w-4 h-4" />
                            </span>
                        </Link>
                    </Reveal>
                </div>
            </section>

            {/* ---------- SUBMISSION FORM ---------- */}
            <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-24 sm:pt-32">
                <div className="grid lg:grid-cols-12 gap-12 items-start">
                    <div className="lg:col-span-5">
                        <Reveal>
                            <p className="font-mono-brand text-xs uppercase tracking-[0.25em] text-pink">
                                Drop your questions here
                            </p>
                            <h2 className="mt-3 font-display font-black text-2xl sm:text-3xl lg:text-4xl text-ink tracking-tight leading-tight">
                                Ask us anything. Or tell us everything.
                            </h2>
                            <p className="mt-6 text-base leading-relaxed text-ink/80">
                                Questions for upcoming guests, stories you think the archive
                                needs, confessions, curiosities. This is a community record,
                                and your voice belongs in it.
                            </p>
                            <p className="mt-4 text-sm text-ink/60">
                                Prefer email? Write to{" "}
                                <a
                                    href={`mailto:${settings.contact_email}`}
                                    data-testid="question-email-link"
                                    className="font-bold text-pink underline underline-offset-4"
                                >
                                    {settings.contact_email}
                                </a>
                            </p>
                        </Reveal>
                    </div>
                    <Reveal delay={0.15} className="lg:col-span-7">
                        <SubmissionForm
                            idPrefix="question-form"
                            heading="Write to BOMGAY"
                            blurb="Everything you send lands straight with the host."
                        />
                    </Reveal>
                </div>
            </section>

            {/* ---------- LISTEN ---------- */}
            <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-24 sm:pt-32 pb-4">
                <Reveal>
                    <div className="bg-ink text-paper border-2 border-ink hard-shadow-pink torn-bottom px-6 sm:px-12 py-14 sm:py-20 text-left">
                        <p className="font-mono-brand text-xs uppercase tracking-[0.25em] text-mustard">
                            Listen everywhere
                        </p>
                        <h2 className="mt-4 font-display font-black text-2xl sm:text-3xl lg:text-4xl tracking-tight">
                            Your next listen starts here.
                        </h2>
                        <p className="mt-4 max-w-xl text-sm sm:text-base leading-relaxed text-paper/75">
                            Find a conversation that stays with you. Listen, explore and
                            discover another part of the queer story.
                        </p>
                        <div className="mt-9 flex flex-wrap gap-4">
                            <a
                                href="https://open.spotify.com/show/3geWbRZ7xGugrzjV5ZbHvB"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="listen-spotify-btn"
                                className="inline-flex items-center gap-2 bg-paper text-ink font-sub font-bold px-6 py-3.5 border-2 border-ink hard-shadow-sm transition-transform duration-200 hover:-translate-y-1 hover:bg-pink hover:text-cream"
                            >
                                Spotify <ArrowUpRight className="w-4 h-4" />
                            </a>
                            <a
                                href="https://www.youtube.com/@Bomgayy"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="listen-youtube-btn"
                                className="inline-flex items-center gap-2 bg-paper text-ink font-sub font-bold px-6 py-3.5 border-2 border-ink hard-shadow-sm transition-transform duration-200 hover:-translate-y-1 hover:bg-pink hover:text-cream"
                            >
                                YouTube <ArrowUpRight className="w-4 h-4" />
                            </a>
                            <a
                                href="https://podcasts.apple.com/in/podcast/bomgay-in/id6823312161"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="listen-apple-btn"
                                className="inline-flex items-center gap-2 bg-paper text-ink font-sub font-bold px-6 py-3.5 border-2 border-ink hard-shadow-sm transition-transform duration-200 hover:-translate-y-1 hover:bg-pink hover:text-cream"
                            >
                                Apple Podcasts <ArrowUpRight className="w-4 h-4" />
                            </a>
                        </div>
                    </div>
                </Reveal>
            </section>
        </div>
    );
}
